import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";
import { uploadPhoto as uploadUMPhoto } from "../../services/university-admin/api";
import optimizeImageFile from "../../utils/imageOptimizer";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMClubForm = ({ club, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("clubs");
  const { t: tErrors } = useTranslation("errors");
  const { showSuccess, showError } = useNotification();
  const { can } = useUniAdminAuth();
  const canListCampuses = can(PERMISSIONS.campus.list);

  const [formData, setFormData] = useState({
    nameEn: "",
    nameLocal: "",
    descriptionEn: "",
    descriptionLocal: "",
    campusId: "",
    status: true,
    price: "",
    imageId: "",
    communication: {
      email: "",
      phoneNumber: "",
      instagramUrl: "",
      twitterUrl: "",
      websiteUrl: "",
    },
  });

  const [campuses, setCampuses] = useState([]);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!club) return;

    setSelectedFile(null);
    setFormData({
      nameEn: club.nameEn || "",
      nameLocal: club.nameLocal || "",
      descriptionEn: club.descriptionEn || "",
      descriptionLocal: club.descriptionLocal || "",
      campusId: club.campusId ? String(club.campusId) : "",
      status: club.status !== false,
      price: club.price != null ? String(club.price) : "",
      imageId: club.imageId ? String(club.imageId) : "",
      communication: {
        email: club.communication?.email || "",
        phoneNumber: club.communication?.phoneNumber || "",
        instagramUrl: club.communication?.instagramUrl || "",
        twitterUrl: club.communication?.twitterUrl || "",
        websiteUrl: club.communication?.websiteUrl || "",
      },
    });

    if (club.imageUrl) setImagePreview(club.imageUrl);
  }, [club]);

  useEffect(() => {
    if (!canListCampuses) return;
    umService.getAllCampuses({}).then((res) => {
      setCampuses(res.success ? res.data || [] : []);
    });
  }, [canListCampuses]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError(t("validation.invalidImageType"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError(t("validation.imageTooLarge"));
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;
    try {
      setUploadingImage(true);
      const fileToSend = await optimizeImageFile(selectedFile);
      const response = await uploadUMPhoto(fileToSend);
      if (response.success) {
        showSuccess(t("validation.imageUploadSuccess"));
        return response.data.imageId;
      }
      return null;
    } catch (error) {
      console.error("Error uploading image:", error);
      showError(t("validation.imageUploadFailed"));
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.nameEn.trim())
      nextErrors.nameEn = t("validation.nameEnRequired");
    if (!formData.nameLocal.trim())
      nextErrors.nameLocal = t("validation.nameLocalRequired");
    if (!formData.campusId)
      nextErrors.campusId = t("validation.campusRequired");

    if (
      formData.communication.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.communication.email)
    ) {
      nextErrors.contactEmail = t("validation.invalidEmail");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let imageId = formData.imageId;
    if (selectedFile) {
      const uploadedImageId = await uploadImage();
      if (uploadedImageId) {
        imageId = uploadedImageId;
      } else {
        return;
      }
    }

    const payload = {
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
      descriptionEn: formData.descriptionEn.trim(),
      descriptionLocal: formData.descriptionLocal.trim(),
      campusId: formData.campusId,
      imageId: imageId || null,
      communication: {
        email: formData.communication.email.trim(),
        phoneNumber: formData.communication.phoneNumber.trim(),
        instagramUrl: formData.communication.instagramUrl.trim(),
        twitterUrl: formData.communication.twitterUrl.trim(),
        websiteUrl: formData.communication.websiteUrl.trim(),
      },
    };

    if (formData.price !== "") {
      payload.price = parseFloat(formData.price) || 0;
    }

    if (club) {
      payload.status = Boolean(formData.status);
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="club-form">
      <div className="row g-4">
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-users"></i>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.englishName")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
                  placeholder={t("form.nameEnPlaceholder")}
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, nameEn: e.target.value }))
                  }
                  disabled={loading || uploadingImage}
                />
                {errors.nameEn && (
                  <div className="invalid-feedback">{errors.nameEn}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.localName")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
                  placeholder={t("form.nameLocalPlaceholder")}
                  value={formData.nameLocal}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      nameLocal: e.target.value,
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
                {errors.nameLocal && (
                  <div className="invalid-feedback">{errors.nameLocal}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.descriptionEn")}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={t("form.descriptionEnPlaceholder")}
                  value={formData.descriptionEn}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      descriptionEn: e.target.value,
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.descriptionLocal")}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={t("form.descriptionLocalPlaceholder")}
                  value={formData.descriptionLocal}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      descriptionLocal: e.target.value,
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.campus")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
                  value={formData.campusId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      campusId: e.target.value,
                    }))
                  }
                  disabled={loading || uploadingImage || !canListCampuses}
                >
                  <option value="">{t("allCampuses")}</option>
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.nameEn}
                    </option>
                  ))}
                </select>
                {errors.campusId && (
                  <div className="invalid-feedback">{errors.campusId}</div>
                )}
                {!canListCampuses && (
                  <small className="text-danger">{tErrors("permission.denied")}</small>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold">
                  {t("fields.status")}
                </label>
                <select
                  className="form-select"
                  value={String(formData.status)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value === "true",
                    }))
                  }
                  disabled={loading || uploadingImage}
                >
                  <option value="true">{t("activeClub")}</option>
                  <option value="false">{t("inactiveClub")}</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold">Price (₺)</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, price: e.target.value }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-address-card"></i>
              <h6>{t("contactInformation", "Contact Information")}</h6>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">Email</label>
                <input
                  type="email"
                  className={`form-control ${errors.contactEmail ? "is-invalid" : ""}`}
                  value={formData.communication.email}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      communication: {
                        ...prev.communication,
                        email: e.target.value,
                      },
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
                {errors.contactEmail && (
                  <div className="invalid-feedback">{errors.contactEmail}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">Phone</label>
                <input
                  className="form-control"
                  value={formData.communication.phoneNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      communication: {
                        ...prev.communication,
                        phoneNumber: e.target.value,
                      },
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-bold">Instagram</label>
                <input
                  className="form-control"
                  value={formData.communication.instagramUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      communication: {
                        ...prev.communication,
                        instagramUrl: e.target.value,
                      },
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-bold">Twitter</label>
                <input
                  className="form-control"
                  value={formData.communication.twitterUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      communication: {
                        ...prev.communication,
                        twitterUrl: e.target.value,
                      },
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-bold">Website</label>
                <input
                  className="form-control"
                  value={formData.communication.websiteUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      communication: {
                        ...prev.communication,
                        websiteUrl: e.target.value,
                      },
                    }))
                  }
                  disabled={loading || uploadingImage}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-image"></i>
              <h6>{t("form.clubImage")}</h6>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.uploadClubImage")}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading || uploadingImage}
                />
                <small className="text-muted">{t("form.imageFormats")}</small>
                {uploadingImage && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      <small className="text-primary">
                        {t("form.uploadingImage")}
                      </small>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                {imagePreview && (
                  <div>
                    <label className="form-label fw-bold">
                      {t("form.imagePreview")}
                    </label>
                    <div className="club-logo-preview">
                      <img src={imagePreview} alt={t("form.imagePreview")} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading || uploadingImage}
        >
          {t("common:buttons.cancel", "Cancel")}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || uploadingImage}
        >
          {uploadingImage
            ? t("form.uploadingImage")
            : loading
              ? t("common:actions.saving", "Saving...")
              : t("common:buttons.save", "Save")}
        </button>
      </div>
    </form>
  );
};

export default UMClubForm;
